import React from 'react';
import {
  TabbedShowLayout,
  Tab,
  ReferenceField,
  useShowController,
  useTranslate,
  usePermissions,
} from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { Show } from '~/layout';
import { NoPermissions, TextField } from '~/components/ra';
import {
  TextField as TextFieldComponent,
  StatusField,
  H2,
  ChipField,
  BooleanField,
} from '~/components';
import IdentityAuditLog from './IdentityAuditLog';
import { Identity } from '~/types';
import IdentityActions from './components/IdentityActions';
import { isEmpty } from 'lodash';
import { Longdash } from '~/utils';

export const l = (key: string) => `resources.team.fields.${key}`;

const IdentityShow = (props: any) => {
  const { permissions } = usePermissions();

  const showController = useShowController(props);
  const showRecord: Identity | undefined = showController.record;

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const classes = useStyles();

  if (!permissions) return null;
  if (!permissions.includes('identity.view')) return <NoPermissions />;

  const is2FaEnabled =
    showRecord?.totp ||
    !!(showRecord?.requiredActions || []).find((item) => item === 'CONFIGURE_TOTP');
  const emailVerified = !!showRecord?.emailVerified;

  return (
    <Show {...props} actions={null}>
      <TabbedShowLayout className={classes.tabbedShowLayout}>
        <Tab label={l('profile')}>
          <div className={classes.section}>
            <Grid
              container
              className={classes.bottomSpacing2}
              justify='space-between'
              alignItems='center'
            >
              <Grid item xs={8}>
                <H2 topSpacing={0} bottomSpacing={0}>
                  {t('personalData')}
                </H2>
              </Grid>
              <Grid item container xs={4} spacing={2} id='section-actions' justify='flex-end'>
                <IdentityActions identityRecord={showRecord} permissions={permissions} />
              </Grid>
            </Grid>
            <div className={classes.container}>
              <Grid container item spacing={2} xs={10}>
                <Grid item xs={4}>
                  <TextField source='username' label={t('username')} {...showController} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='firstName' label={t('firstName')} {...showController} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='lastName' label={t('lastName')} {...showController} />
                </Grid>
                <Grid item xs={4}>
                  {showRecord?.deleted ? (
                    <TextFieldComponent label={t('status')}>
                      <StatusField status='DISABLED' />
                    </TextFieldComponent>
                  ) : (
                    <TextFieldComponent label={t('status')}>
                      <StatusField status='ENABLED' />
                    </TextFieldComponent>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <BooleanField value={is2FaEnabled} label={t('2FA')} />
                </Grid>
                <Grid item xs={4}>
                  <BooleanField value={emailVerified} label={t('emailVerified')} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='email' label={t('email')} {...showController} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='phoneNumber' label={t('phoneNumber')} {...showController} />
                </Grid>
                <Grid item xs={4}>
                  <TextFieldComponent label={t('requiredActions')} multiline>
                    {!isEmpty(showRecord?.requiredActions)
                      ? showRecord?.requiredActions?.map((item: any) => {
                          return <ChipField label={item} />;
                        })
                      : Longdash}
                  </TextFieldComponent>
                </Grid>
                <Grid item xs={4}>
                  <ReferenceField
                    source='roleId'
                    reference='roles'
                    link={false}
                    {...showController}
                  >
                    <TextField source='name' label={t('roleId')} />
                  </ReferenceField>
                </Grid>
              </Grid>
            </div>
          </div>
        </Tab>
        <Tab label={l('actionLog')} path='actionLog'>
          <IdentityAuditLog identityId={showRecord?.id as string} />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const useStyles = makeStyles((theme) => ({
  tabbedShowLayout: {
    minHeight: 500,
  },
  container: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  section: {
    '& #section-actions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& #section-actions': {
        opacity: 1,
      },
    },
  },
  bottomSpacing2: {
    marginBottom: theme.spacing(2),
  },
}));

export default IdentityShow;
